// The loadout rack: pick a primary, a secondary and a grenade. Pad first (DualSense): ↑↓ picks
// the row, ←→ the item, ✕ locks it in, ○ backs out unchanged. Mouse and keyboard work too.
// Self-contained DOM overlay with its own gamepad poll, so the saloon can open it over the 3D
// scene from the gun rack or the bartender, and the lobby can open it too. Saloon only: the server
// ignores loadout changes once the run's first wave is out (Game.loadoutOpen).
//
//   const h = openLoadout(document.body, current, (l) => …, () => …, { cls });
//   h.close();

import { CLASSES, ClassId } from '../../sim/defs';
import { CLASS_LOADOUT, Fire, GRENADES, Loadout, PRIMARIES, SECONDARIES, WEAPONS, WeaponDef, validLoadout } from '../../sim/weapons';
import { firstPad, psGlyph } from '../input/padnav';

const KEY = 'emg.loadout';
const AP_NAME = ['AP1', 'AP2', 'AP3', 'AP4', 'AP5'];

export function loadStoredLoadout(cls: ClassId): Loadout {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Partial<Loadout>>;
    return validLoadout(all[cls] ?? null, cls);
  } catch { return { ...CLASS_LOADOUT[cls] }; }
}
export function saveLoadout(cls: ClassId, l: Loadout) {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Loadout>;
    all[cls] = l;
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch { /* private mode */ }
}

export interface LoadoutHandle { close(): void; readonly open: boolean }

// ------------------------------------------------------------------ stats
function dps(w: WeaponDef) {
  const per = w.damage * w.pellets;
  const tick = 1 / 60;
  if (w.fire === Fire.Burst) return (per * w.burst) / ((w.interval + w.burstGap * (w.burst - 1)) * tick);
  if (w.fire === Fire.Charge) return per / ((w.charge + w.interval) * tick);
  return per / (w.interval * tick);
}
function bars(w: WeaponDef) {
  return [
    ['DAMAGE', Math.min(1, (w.damage * w.pellets) / 170)],
    ['FIRE RATE', Math.min(1, dps(w) / 180)],
    ['RANGE', Math.min(1, Math.min(w.falloffEnd, w.range) / 180)],
    ['MAGAZINE', Math.min(1, w.mag / 40)],
  ] as [string, number][];
}

const CSS = `
.lo-wrap { position: fixed; inset: 0; z-index: 40; display: flex; align-items: center; justify-content: center;
  background: radial-gradient(ellipse at 50% 110%, rgba(250,199,125,0.18), transparent 60%), rgba(10,12,11,0.55);
  font-family: 'Space Grotesk', system-ui, sans-serif; color: #f0e6c8; }
.lo-panel { width: min(1180px, calc(100vw - 24px)); max-height: calc(100vh - 24px); overflow: auto; padding: 22px 26px 18px;
  background: rgba(14,18,16,0.72); border: 1px solid rgba(250,199,125,0.35); border-radius: 4px; backdrop-filter: blur(6px);
  box-shadow: 0 30px 80px rgba(0,0,0,0.45); }
.lo-title { font-family: 'Space Grotesk', sans-serif; font-weight: 600; letter-spacing: 0.5px; color: #fac77d; font-size: 34px; margin: 0; line-height: 1; }
.lo-sub { color: rgba(240,230,200,0.7); letter-spacing: 3px; font-size: 12px; text-transform: uppercase; margin: 6px 0 14px; }
.lo-row { margin: 10px 0 4px; }
.lo-row > .lo-label { font-size: 12px; letter-spacing: 3px; color: rgba(240,230,200,0.55); text-transform: uppercase; margin-bottom: 6px; }
.lo-row.on > .lo-label { color: #ffe6b0; }
.lo-items { display: flex; gap: 8px; overflow-x: auto; padding: 4px 2px 8px; scrollbar-width: thin; }
.lo-item { flex: 0 0 auto; min-width: 150px; padding: 10px 12px 9px; border: 1px solid rgba(240,230,200,0.15); background: rgba(0,0,0,0.28);
  border-radius: 2px; cursor: pointer; transition: background .1s, border-color .1s; }
.lo-item h4 { margin: 0; font-size: 19px; letter-spacing: 0.5px; white-space: nowrap; }
.lo-item .lo-tag { font-size: 11px; letter-spacing: 2px; color: rgba(240,230,200,0.55); text-transform: uppercase; white-space: nowrap; }
.lo-item.sel { border-color: rgba(250,199,125,0.7); background: rgba(250,199,125,0.1); }
.lo-item.sel h4::after { content: ' \\2713'; color: #fac77d; font-size: 15px; }
.lo-row.on .lo-item.cur { outline: 3px solid #ffe6b0; outline-offset: 2px; background: rgba(250,199,125,0.22); box-shadow: 0 0 18px rgba(250,199,125,0.35); }
.lo-detail { display: grid; grid-template-columns: 1.3fr 1fr; gap: 18px; margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(250,199,125,0.2); min-height: 120px; }
@media (max-width: 760px) { .lo-detail { grid-template-columns: 1fr; } }
.lo-name { font-size: 26px; font-weight: 700; color: #ffe6b0; letter-spacing: 1px; }
.lo-name small { font-size: 14px; color: rgba(240,230,200,0.6); letter-spacing: 2px; margin-left: 8px; text-transform: uppercase; }
.lo-blurb { font-size: 16px; line-height: 1.35; margin-top: 4px; color: rgba(240,230,200,0.9); }
.lo-chips { margin-top: 8px; display: flex; gap: 6px; flex-wrap: wrap; }
.lo-chip { font: 700 11px 'Space Grotesk'; letter-spacing: 1.5px; padding: 2px 8px; border: 1px solid rgba(250,199,125,0.45); border-radius: 9px; color: #fac77d; }
.lo-bar { display: grid; grid-template-columns: 90px 1fr; gap: 10px; align-items: center; font-size: 12px; letter-spacing: 2px; color: rgba(240,230,200,0.6); margin: 5px 0; }
.lo-bar i { display: block; height: 6px; background: rgba(240,230,200,0.12); position: relative; }
.lo-bar i b { position: absolute; inset: 0 auto 0 0; background: linear-gradient(90deg, #d99423, #ffe6b0); }
.lo-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 14px; font-size: 15px; color: rgba(240,230,200,0.8); }
.lo-foot .lo-hint { color: rgba(240,230,200,0.6); font-size: 13px; }
.lo-btn { font: 700 17px 'Space Grotesk'; letter-spacing: 3px; text-transform: uppercase; padding: 9px 22px; background: rgba(250,199,125,0.14); border: 1px solid #fac77d;
  color: #ffe6b0; border-radius: 2px; cursor: pointer; }
.lo-btn.ghost { border-color: rgba(240,230,200,0.3); color: #f0e6c8; background: transparent; }
.lo-btn .psg { margin-right: 8px; width: 1.15em; height: 1.15em; vertical-align: -0.22em; }
.lo-dance { font-size: 13px; color: rgba(240,230,200,0.65); margin-top: 2px; }
.lo-dance b { color: #ff3cac; }
`;

let cssDone = false;
function injectCss() {
  if (cssDone) return;
  cssDone = true;
  const s = document.createElement('style');
  s.textContent = CSS;
  document.head.appendChild(s);
}

interface Row { label: string; ids: number[]; kind: 'c' | 'w' | 'g' }

export function openLoadout(host: HTMLElement, current: Loadout, onDone: (l: Loadout) => void, onCancel?: () => void, opts: { cls?: ClassId; sound?: (name: 'ui_move' | 'ui_select') => void; onClass?: (c: ClassId) => void } = {}): LoadoutHandle {
  injectCss();
  let cls = opts.cls ?? ClassId.Gunslinger;
  const startCls = cls;
  const pick: Loadout = validLoadout(current, cls);
  // the class rides the top row when the caller can swap it (the saloon's gun rack)
  const withClass = !!opts.onClass;
  const rows: Row[] = [
    ...(withClass ? [{ label: 'Class', ids: CLASSES.map((_, i) => i), kind: 'c' as const }] : []),
    { label: 'Primary', ids: PRIMARIES, kind: 'w' },
    { label: 'Secondary', ids: SECONDARIES, kind: 'w' },
    { label: 'Grenade', ids: GRENADES.map((g) => g.id), kind: 'g' },
  ];
  const off = withClass ? 1 : 0;
  const key = (r: number): keyof Loadout => (r - off === 0 ? 'primary' : r - off === 1 ? 'secondary' : 'grenade');
  const cur = (r: number) => (rows[r].kind === 'c' ? cls : pick[key(r)]);
  const cursor = rows.map((r, i) => Math.max(0, r.ids.indexOf(cur(i))));
  let row = 0;
  let isOpen = true;

  const wrap = document.createElement('div');
  wrap.className = 'lo-wrap';
  let c = CLASSES[cls];
  wrap.innerHTML = `
    <div class="lo-panel" role="dialog" aria-label="Loadout">
      <h2 class="lo-title">The Gun Rack</h2>
      <div class="lo-sub">${c.name} · G.U.L.P.-issued, liquidation-grade</div>
      <div class="lo-dance">Robots die fastest to a two-step: <b>PRIME</b> with your class move (${c.primer.toLowerCase()}), then <b>DETONATE</b> (${c.detonator.toLowerCase()}). Any gun works with any class.</div>
      <div class="lo-rows"></div>
      <div class="lo-detail"></div>
      <div class="lo-foot">
        <span class="lo-hint">Special weapons aren't picked here: they come out of ordnance crates during the run.</span>
        <span>
          <button class="lo-btn ghost" data-act="cancel">${psGlyph('circle')}Back</button>
          <button class="lo-btn" data-act="done">${psGlyph('cross')}Lock it in</button>
        </span>
      </div>
    </div>`;
  const rowsEl = wrap.querySelector('.lo-rows') as HTMLElement;
  const detail = wrap.querySelector('.lo-detail') as HTMLElement;

  rows.forEach((r, ri) => {
    const re = document.createElement('div');
    re.className = 'lo-row';
    re.innerHTML = `<div class="lo-label">${r.label}</div><div class="lo-items"></div>`;
    const items = re.querySelector('.lo-items') as HTMLElement;
    r.ids.forEach((id, ii) => {
      const it = document.createElement('div');
      it.className = 'lo-item';
      if (r.kind === 'c') {
        const k = CLASSES[id];
        it.innerHTML = `<h4>${k.name}</h4><div class="lo-tag">${k.role}</div>`;
      } else if (r.kind === 'w') {
        const w = WEAPONS[id];
        it.innerHTML = `<h4>${w.name}</h4><div class="lo-tag">${w.role} · ${AP_NAME[w.ap]}</div>`;
      } else {
        const g = GRENADES[id];
        it.innerHTML = `<h4>${g.name.replace(' Grenade', '')}</h4><div class="lo-tag">${g.damage > 0 ? `${g.damage} DMG` : 'NO DAMAGE'} · ${g.fuse > 0 ? `${g.fuse} S FUSE` : 'IMPACT'}${g.sticky ? ' · STICKY' : ''}</div>`;
      }
      it.onmouseenter = () => { row = ri; cursor[ri] = ii; render(); };
      it.onclick = () => { row = ri; cursor[ri] = ii; choose(); render(); };
      items.appendChild(it);
    });
    rowsEl.appendChild(re);
  });
  (wrap.querySelector('[data-act=done]') as HTMLElement).onclick = () => finish(true);
  (wrap.querySelector('[data-act=cancel]') as HTMLElement).onclick = () => finish(false);

  function choose() {
    if (rows[row].kind === 'c') { setCls(rows[row].ids[cursor[row]] as ClassId); return; }
    (pick as unknown as Record<string, number>)[key(row)] = rows[row].ids[cursor[row]];
  }
  function setCls(k: ClassId) {
    if (k === cls) return;
    cls = k; c = CLASSES[k];
    (wrap.querySelector('.lo-sub') as HTMLElement).textContent = `${c.name} · G.U.L.P.-issued, liquidation-grade`;
    (wrap.querySelector('.lo-dance') as HTMLElement).innerHTML = `Robots die fastest to a two-step: <b>PRIME</b> with your class move (${c.primer.toLowerCase()}), then <b>DETONATE</b> (${c.detonator.toLowerCase()}). Any gun works with any class.`;
  }

  function render() {
    rowsEl.querySelectorAll('.lo-row').forEach((re, ri) => {
      re.classList.toggle('on', ri === row);
      re.querySelectorAll('.lo-item').forEach((it, ii) => {
        it.classList.toggle('cur', ii === cursor[ri]);
        it.classList.toggle('sel', rows[ri].ids[ii] === cur(ri));
        if (ri === row && ii === cursor[ri]) (it as HTMLElement).scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    });
    const id = rows[row].ids[cursor[row]];
    if (rows[row].kind === 'c') {
      const k = CLASSES[id];
      detail.innerHTML = `<div><div class="lo-name">${k.name}<small>${k.role}</small></div><div class="lo-blurb">Prime: ${k.primer.toLowerCase()}. Detonate: ${k.detonator.toLowerCase()}.</div>
        <div class="lo-chips"><span class="lo-chip">${Math.round(k.shieldMax)} SHIELD</span><span class="lo-chip">${Math.round(k.heatMax)} HEAT</span></div></div><div></div>`;
    } else if (rows[row].kind === 'w') {
      const w = WEAPONS[id];
      const mode = w.fire === Fire.Burst ? `${w.burst}-ROUND BURST` : w.fire === Fire.Auto ? 'FULL AUTO' : w.fire === Fire.Charge ? 'CHARGE' : 'SEMI-AUTO';
      const chips = [AP_NAME[w.ap] + ' ARMOR PEN', mode, w.rounds ? 'LOADS BY THE ROUND' : 'MAG RELOAD', w.pellets > 1 ? `${w.pellets} PELLETS` : '', w.shieldMul > 1 ? '2x VS SHIELDS' : '', w.akimbo ? 'AKIMBO' : '']
        .filter(Boolean).map((t) => `<span class="lo-chip">${t}</span>`).join('');
      detail.innerHTML = `<div><div class="lo-name">${w.name}${w.sub ? `<small>${w.sub}</small>` : ''}</div><div class="lo-blurb">${w.blurb}</div><div class="lo-chips">${chips}</div></div>
        <div>${bars(w).map(([n, v]) => `<div class="lo-bar"><span>${n}</span><i><b style="width:${Math.round(v * 100)}%"></b></i></div>`).join('')}</div>`;
    } else {
      const g = GRENADES[id];
      detail.innerHTML = `<div><div class="lo-name">${g.name}</div><div class="lo-blurb">${g.blurb}</div>
        <div class="lo-chips"><span class="lo-chip">CARRY 3</span><span class="lo-chip">${g.fuse > 0 ? `HOLD R1 TO COOK · ${g.fuse} S FUSE` : 'R1: GOES OFF ON IMPACT'}</span>${g.sticky ? '<span class="lo-chip">STICKS TO ROBOTS</span>' : ''}<span class="lo-chip">${Math.round(g.radius)} M BLAST</span><span class="lo-chip">REFILLS FROM SHIELD DAMAGE</span></div></div>
        <div><div class="lo-bar"><span>DAMAGE</span><i><b style="width:${Math.round(Math.min(1, g.damage / 85) * 100)}%"></b></i></div>
        <div class="lo-bar"><span>RADIUS</span><i><b style="width:${Math.round(Math.min(1, g.radius / 9) * 100)}%"></b></i></div>
        <div class="lo-bar"><span>CONTROL</span><i><b style="width:${Math.round(Math.min(1, (g.stun * 2 + g.slow + g.gasSecs + (g.elem >= 0 ? 4 : 0)) / 10) * 100)}%"></b></i></div></div>`;
    }
  }

  function move(dr: number, dc: number) {
    opts.sound?.('ui_move');
    if (dr) row = (row + dr + rows.length) % rows.length;
    if (dc) { const n = rows[row].ids.length; cursor[row] = (cursor[row] + dc + n) % n; choose(); }
    render();
  }

  function finish(ok: boolean) {
    if (!isOpen) return;
    close();
    opts.sound?.(ok ? 'ui_select' : 'ui_move');
    if (ok) { if (cls !== startCls) opts.onClass?.(cls); saveLoadout(cls, pick); onDone({ ...pick }); } else onCancel?.();
  }

  // ---- keyboard
  const onKey = (e: KeyboardEvent) => {
    const k = e.code;
    if (k === 'ArrowUp' || k === 'KeyW') move(-1, 0);
    else if (k === 'ArrowDown' || k === 'KeyS') move(1, 0);
    else if (k === 'ArrowLeft' || k === 'KeyA') move(0, -1);
    else if (k === 'ArrowRight' || k === 'KeyD') move(0, 1);
    else if (k === 'Enter' || k === 'Space') finish(true);
    else if (k === 'Escape' || k === 'Backspace') finish(false);
    else return;
    e.preventDefault(); e.stopPropagation();
  };
  window.addEventListener('keydown', onKey, true);

  // ---- pad: own poll; buttons held when the rack opened are ignored until released
  const held = new Set<number>();
  let first = true, raf = 0, rep = 0, repDir = '';
  const poll = () => {
    if (!isOpen) return;
    const gp = firstPad();
    if (gp) {
      const now = performance.now();
      const down = (i: number) => !!gp.buttons[i]?.pressed;
      const ax = gp.axes[0] ?? 0, ay = gp.axes[1] ?? 0;
      const dir = down(12) || ay < -0.6 ? 'u' : down(13) || ay > 0.6 ? 'd' : down(14) || ax < -0.6 ? 'l' : down(15) || ax > 0.6 ? 'r' : '';
      if (first) { for (let i = 0; i < 16; i++) if (down(i)) held.add(i); first = false; if (dir) { repDir = dir; rep = now + 1e9; } }
      for (const i of [...held]) if (!down(i)) held.delete(i);
      const press = (i: number) => down(i) && !held.has(i) && (held.add(i), true);
      if (dir !== repDir) { repDir = dir; rep = now + 320; if (dir) step(dir); }
      else if (dir && now > rep) { rep = now + 110; step(dir); }
      if (press(0)) finish(true);
      else if (press(1)) finish(false);
      else if (press(4)) move(-1, 0); // L1 / R1 hop rows too
      else if (press(5)) move(1, 0);
    }
    raf = requestAnimationFrame(poll);
  };
  const step = (d: string) => move(d === 'u' ? -1 : d === 'd' ? 1 : 0, d === 'l' ? -1 : d === 'r' ? 1 : 0);
  raf = requestAnimationFrame(poll);

  function close() {
    if (!isOpen) return;
    isOpen = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
  }

  host.appendChild(wrap);
  render();
  return { close, get open() { return isOpen; } };
}
